import {ApiError, PowderguideApiInterface, UserData} from "./powderguide-api";
import {LanguageLabels, Translator} from "./translator";

export class PowderguideAuthentication {
    private _user: UserData|null = null;
    private _error: string|null = null;
    private _loading: Promise<void>|null = null;
    private _initialized: boolean = false;
    private translator: Translator = new Translator({ })

    constructor(
        private powderguideApi: PowderguideApiInterface,
    ) { }

    public get user(): UserData|null {
        return this._user;
    }

    public get error(): string|null {
        return this._error;
    }

    public get loading(): boolean {
        return this._loading !== null;
    }

    public async init(languageLabels: LanguageLabels): Promise<void> {
        this.translator.addLabels(languageLabels);
        if (this._initialized) {
            return;
        }
        if (this._loading !== null) {
            return this._loading;
        }

        this._loading = new Promise(async resolve => {
            this._user = await this.exchangeAppCode() ?? await this.powderguideApi.getUser();
            this._loading = null;
            this._initialized = true;
            resolve();
        })

        return this._loading;
    }

    /**
     * The mobile app opens an article as ?app_code=<one-time code>: log this browser in right on
     * the page, without navigating. The code leaves the address first (history, referrers); every
     * other query parameter and the hash stay. Returns null when there is no code or the exchange
     * failed, so the caller falls back to the stored token (an already signed-in visitor stays so).
     */
    private async exchangeAppCode(): Promise<UserData|null> {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('app_code');
        if (!code) {
            return null;
        }

        params.delete('app_code');
        const query = params.toString();
        window.history.replaceState({}, document.title, window.location.pathname + (query ? `?${query}` : '') + window.location.hash);

        try {
            const user = await this.powderguideApi.handoffExchange(code);
            // The login page runs init() before completeOAuthLogin(), so on the /login?app_code&return
            // fallback this is the call that spends the code: keep honouring ?return (same origin only).
            if (user !== null && params.has('return')) {
                this.goToReturnPathOr(() => {});
            }

            return user;
        } catch (e) {
            // Expired or used code, rate limit, network: not worth a banner on a normal page.
            console.error(e);
            return null;
        }
    }

    async login(username: string, password: string): Promise<void> {
        this._loading = new Promise(async () => {
            try {
                this._user = await this.powderguideApi.login(username, password);
                this._error = this._user === null ? this.translator.translate('errors.generic') : null;
            } catch (e) {
                console.error(e)
                // Only a rejected login (422) means wrong credentials; anything else
                // (expired page/CSRF 419, throttling, server error) is not the user's typo.
                this._error = this.translator.translate(this.loginErrorKey(e));
            }
            if (this._user !== null) {
                this.goToReturnPathOr(() => window.location.reload());
            }

            this._loading = null;
        })
    }

    /**
     * Finish a social login: if the OAuth callback bounced us back here with a
     * one-time ?oauth_code, trade it for a token. On success we reload into the
     * signed-in state; on error we keep the message visible on the login page.
     */
    async completeOAuthLogin(): Promise<void> {
        const params = new URLSearchParams(window.location.search);

        const oauthError = params.get('oauth_error');
        if (oauthError) {
            // email_in_use: an account with this e-mail exists, it was not linked.
            this._error = this.translator.translate(oauthError === 'email_in_use' ? 'errors.email_in_use' : 'errors.generic');
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        const code = params.get('oauth_code');
        if (!code) {
            return;
        }

        try {
            this._user = await this.powderguideApi.oauthExchange(code);
            this._error = null;
            this.goToReturnPathOr(() => window.location.href = window.location.pathname);
        } catch (e) {
            console.error(e);
            // 429 is the rate limit; every other failure (expired code, wrong browser) is generic.
            this._error = this.translator.translate(e instanceof ApiError && e.status === 429 ? 'errors.too_many_attempts' : 'errors.generic');
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }

    private loginErrorKey(e: unknown): string {
        if (e instanceof ApiError && e.status === 422) {
            return 'errors.invalid_credentials';
        }

        return e instanceof ApiError && e.status === 429 ? 'errors.too_many_attempts' : 'errors.generic';
    }

    /** Back to where the sign-in started (?return, e.g. the header icon's page); local paths only. */
    private goToReturnPathOr(fallback: () => void): void {
        const back = new URLSearchParams(window.location.search).get('return');
        const target = back ? new URL(back, window.location.origin) : null;

        if (target && target.origin === window.location.origin) {
            // The resolved absolute URL, not its parts: a pathname like `//evil.com` (from `/.//evil.com`) would be read as a host.
            window.location.href = target.href;
        } else {
            fallback();
        }
    }

    async logout(): Promise<void> {
        this._user = null;
        await this.powderguideApi.logout();
        window.location.reload();
    }
}
